import { TypeInitProps, TypeRequest } from '../../types';
import { encryptParams } from '../../utils/index';
import { WEB_URL } from '../../utils/urls';

export default class OpenAPI {
  private request: TypeRequest;
  constructor(props: TypeInitProps & { request: TypeRequest }) {
    const { request } = props;
    this.request = request;
  }
  /**
   * [get auth info]
   * @param {string} client_key key of third-party app
   * @param {string} scope list of authorities
   * @return {Promise} request result
   */
  public getAuthInfo(params: {
    client_key: string;
    scope: string;
    extra_params?: object;
  }): Promise<any> {
    let { client_key, scope, extra_params = {} } = params;
    return this.request({
      url: WEB_URL.GET_OPEN_AUTH_INFO,
      method: 'GET',
      data: {
        client_key,
        scope,
        ...extra_params,
      },
    });
  }

  /**
   * [send code]
   * @param {string} mobile - the mobile used to login
   * @return {Promise} send result
   */
  public sendCode(params: {
    mobile: string;
    extra_params?: object;
  }): Promise<any> {
    let { mobile, extra_params = {} } = params;
    let data = encryptParams({ mobile, type: 27, ...extra_params }, [
      'mobile',
      'type',
    ]);
    return this.request({
      url: WEB_URL.OPEN_SEND_CODE,
      method: 'GET',
      data,
    });
  }

  /**
   * [sms authorization]
   * @param {string} client_key key of third-party app
   * @param {string} scope list of authorities
   * @param {string} source source of authorization
   * @param {string} redirect_uri redirect url after successful authorization
   * @param {string} state security check
   * @param {string} mobile mobile used to authorize
   * @param {string} code corresponding sms code
   * @return {Promise} auth result
   */
  public smsAuth(params: {
    client_key: string;
    scope: string;
    source: string;
    redirect_uri?: string;
    state?: string;
    mobile: string;
    code: string;
    extra_params?: object;
  }): Promise<any> {
    let {
      client_key,
      scope,
      source,
      redirect_uri,
      state,
      mobile,
      code,
      extra_params = {},
    } = params;
    let data = encryptParams(
      {
        client_key,
        scope,
        source,
        redirect_uri,
        state,
        mobile,
        code,
        ...extra_params,
      },
      ['mobile', 'code']
    );
    return this.request({
      url: WEB_URL.OPEN_SMS_AUTH,
      method: 'POST',
      data,
    });
  }

  /**
   * [account authorization]
   * @param {string} client_key key of third-party app
   * @param {string} scope list of authorities
   * @param {string} source source of authorization
   * @param {string} redirect_uri redirect url after successful authorization
   * @param {string} state security check
   * @param {string} account account used to authorize
   * @param {string} password corresponding password
   * @return {Promise} auth result
   */
  public accountAuth(params: {
    client_key: string;
    scope: string;
    source: string;
    redirect_uri?: string;
    state?: string;
    password: string;
    account: string;
    extra_params?: object;
  }): Promise<any> {
    let {
      client_key,
      scope,
      source,
      redirect_uri,
      state,
      password,
      account,
      extra_params = {},
    } = params;
    let data = encryptParams(
      {
        client_key,
        scope,
        source,
        redirect_uri,
        state,
        password,
        account,
        ...extra_params,
      },
      ['password', 'account']
    );
    return this.request({
      url: WEB_URL.OPEN_ACCOUNT_AUTH,
      method: 'POST',
      data,
    });
  }

  /**
   * [third-party oauth authorization]
   * @param {string} client_key key of third-party app
   * @param {string} scope list of authorities
   * @param {string} source source of authorization
   * @param {string} redirect_uri redirect url after successful authorization
   * @param {string} state security check
   * @param {number} platform_app_id the third appId
   * @param {string} access_token token of third-party authorization
   * @param {string} access_token_secret token of google authorization
   * @param {string} code code of third-party authorization
   * @param {number} expires_in expire time
   * @param {number} openid openid of third platform
   * @param {string} profile_key - third-party ticket that preserves information temporaily
   * @param {string} platform - the platform used to authorize
   * @return {Promise} auth result
   */
  public thirdAuth(params: {
    client_key: string;
    scope: string;
    source: string;
    redirect_uri?: string;
    state?: string;
    platform_app_id: number;
    access_token?: string;
    access_token_secret?: string;
    code?: string;
    expires_in?: number;
    openid?: number;
    profile_key?: string;
    platform?: string;
    extra_params?: object;
  }): Promise<any> {
    let { extra_params = {}, ...normalParams } = params;
    return this.request({
      url: WEB_URL.OPEN_THIRD_AUTH,
      method: 'POST',
      data: {
        ...normalParams,
        ...extra_params,
      },
    });
  }
  /**
   * [open get qrcode]
   * @param {string} client_key key of third-party app
   * @param {string} scope list of authorities
   * @param {string} state security check
   * @param {string} next callback address of third-party app
   * @returns {Promise} get result
   */
  public getQRCode(params: {
    client_key: string;
    scope: string;
    state: string;
    next: string;
    extra_params?: object;
  }): Promise<any> {
    let { client_key, scope, state, next, extra_params = {} } = params;
    return this.request({
      url: WEB_URL.OPEN_GET_QRCODE,
      method: 'POST',
      data: {
        client_key,
        scope,
        state,
        next,
        ...extra_params,
      },
    });
  }
  /**
   * [check the state of qrcode]
   * @param {string} token identifier of qrcode
   * @param {string} next callback address of third-party app
   * @param {string} scope list of authorities
   * @param {string} client_key key of third -party app
   * @returns {Promise} request result
   */
  public checkQRCode(params: {
    token: string;
    next: string;
    scope: string;
    client_key: string;
    extra_params?: object;
  }): Promise<any> {
    let { token, next, scope, client_key, extra_params = {} } = params;
    return this.request({
      url: WEB_URL.OPEN_CHECK_QRCODE,
      method: 'POST',
      data: {
        token,
        next,
        scope,
        client_key,
        ...extra_params,
      },
    });
  }
  /**
   * [request the authorization page]
   * @returns {Promise} request result
   */
  public toScanQRCode(params: { extra_params?: object }): Promise<any> {
    let { extra_params = {} } = params;
    return this.request({
      url: WEB_URL.OPEN_TO_SCAN_QRCODE,
      method: 'POST',
      data: {
        ...extra_params,
      },
    });
  }
  /**
   * [change state of the qrcode]
   * @param {string} token identifier of qrcode
   * @returns {Promise} request result
   */
  public scanQRCode(params: {
    token: string;
    extra_params?: object;
  }): Promise<any> {
    let { token, extra_params = {} } = params;
    return this.request({
      url: WEB_URL.OPEN_SCAN_QRCODE,
      method: 'POST',
      data: {
        token,
        ...extra_params,
      },
    });
  }
  /**
   * [user confirm to give authorization]
   * @param {string} token identifier of qrcode
   * @param {string} scopes list of authorities
   * @returns {Promise} confirm result
   */
  public confirmQRCode(params: {
    token: string;
    scopes?: string;
    extra_params?: object;
  }): Promise<any> {
    let { token, scopes, extra_params = {} } = params;
    return this.request({
      url: WEB_URL.OPEN_CONFIRM_QRCODE,
      method: 'POST',
      data: {
        token,
        scopes,
        ...extra_params,
      },
    });
  }

  /**
   * [third-party oauth authorization]
   * @param {string} client_key key of third-party app
   * @param {string} scope list of authorities
   * @param {string} source source of authorization
   * @param {string} redirect_uri redirect url after successful authorization
   * @param {string} state security check
   * @param {string} device_platform device platform
   * @param {string} extra_info
   * @param {string} skip_confirm
   * @return {Promise} auth result
   */
  public webAuth(params: {
    client_key: string;
    scope: string;
    source: string;
    redirect_uri?: string;
    state?: string;
    device_platform?: string;
    extra_info?: string;
    skip_confirm?: boolean;
    extra_params?: object;
  }): Promise<any> {
    let { extra_params = {}, ...normalParams } = params;
    return this.request({
      url: WEB_URL.OPEN_WEB_AUTH,
      method: 'POST',
      data: {
        ...normalParams,
        ...extra_params,
      },
    });
  }
}
